import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowDownToLine, Inbox, Layers, Loader, Package } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { consolidationService } from '../../../../../services/consolidationService';
import { ConsolidationAnalysisResponse, ConsolidationSuggestion } from '../../../../../types/consolidation';
import { ConfirmLine } from '../types';
import { parsedQty } from '../utils';
import { useSourceSelections } from '../useSourceSelections';
import { OpenCapacityArgs } from '../useCapacityUpdate';
import { CapacityConfidenceBadge, FillMeter } from './CapacityIndicators';
import { SourceList } from './SourceList';

interface PullInViewProps {
    binCode: string;
    warehouseID: string;
    /** Bumpes av ConsolidationApp etter utført flytt → re-laster forslagene. */
    refreshToken: number;
    onConfirm: (lines: ConfirmLine[]) => void;
    /** Bytt til «Frigjør denne» for samme bin (motsatt retning). */
    onSwitchToEmpty: () => void;
    /** Åpne «Oppdater kapasitet» for (lokasjon, vare). */
    onUpdateCapacity?: (args: OpenCapacityArgs) => void;
}

/**
 * Visning A — «Trekk inn hit» (destinasjon-først).
 * Operatøren har skannet mål-lokasjonen; systemet foreslår varer som kan
 * trekkes inn dit (gruppert per vare, med kapasitetsinfo per forslag).
 */
export const PullInView: React.FC<PullInViewProps> = ({
    binCode,
    warehouseID,
    refreshToken,
    onConfirm,
    onSwitchToEmpty,
    onUpdateCapacity
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [analysis, setAnalysis] = useState<ConsolidationAnalysisResponse | null>(null);

    const suggestions = useMemo<ConsolidationSuggestion[]>(
        () => analysis?.suggestions ?? [],
        [analysis]
    );
    const { selectionFor, toggle, setQty, selectedCount } = useSourceSelections(suggestions);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await consolidationService.getPullCandidates(binCode, warehouseID);
                if (cancelled) return;
                if (!response.success) {
                    setError(response.errorMessage || t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                    setAnalysis(null);
                } else {
                    setAnalysis(response);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                setAnalysis(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [binCode, warehouseID, refreshToken]);

    const handleConfirm = () => {
        const lines: ConfirmLine[] = [];
        suggestions.forEach((suggestion, index) => {
            suggestion.sources.forEach((source) => {
                const selection = selectionFor(index, source.fromBinCode);
                if (!selection.checked || parsedQty(selection.qty) <= 0) return;
                lines.push({
                    id: `${suggestion.itemNo}|${source.fromBinCode}|${suggestion.target.binCode}`,
                    fromBinCode: source.fromBinCode,
                    toBinCode: suggestion.target.binCode,
                    originalToBinCode: suggestion.target.binCode,
                    itemNo: suggestion.itemNo,
                    description1: suggestion.description1,
                    quantity: selection.qty,
                    maxQuantity: source.quantity,
                    freesBin: source.freesBin,
                    capacityConfidence: suggestion.target.capacityConfidence,
                    status: 'pending'
                });
            });
        });
        if (lines.length > 0) onConfirm(lines);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 bg-white rounded-lg border shadow-sm">
                <Loader className="animate-spin h-6 w-6 text-cyan-500" />
                <span className="ml-2 text-sm text-gray-600">
                    {t('warehouse.modules.consolidation.pullIn.loading', {}, 'Henter forslag…')}
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start text-sm text-red-800">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="break-words">{error}</span>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Bin-kort: mål-lokasjonen med kapasitetsinfo per forslag/vare */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-cyan-200">
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                        <ArrowDownToLine className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                        <span className="text-xs uppercase tracking-wide text-gray-500">
                            {t('warehouse.modules.consolidation.pullIn.targetBin', {}, 'Mål-lokasjon')}
                        </span>
                    </div>
                    <button
                        onClick={onSwitchToEmpty}
                        className="flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 min-h-[44px]"
                    >
                        {t('warehouse.modules.consolidation.pullIn.switchToEmpty', {}, '← Frigjør denne i stedet')}
                    </button>
                </div>
                <div className="font-mono text-xl font-semibold text-gray-900 break-all mb-2">{binCode}</div>

                {suggestions.length > 0 && (
                    <div className="space-y-2">
                        {suggestions.map((suggestion, index) => {
                            const needsCapacity = suggestion.target.capacityConfidence !== 'observed'
                                || suggestion.sources.some((s) => s.capacityLimited);
                            return (
                                <div key={`${suggestion.itemNo}-${index}`} className="flex items-center gap-3">
                                    <span className="font-mono text-sm text-gray-700 w-28 flex-shrink-0 truncate" title={suggestion.itemNo}>
                                        {suggestion.itemNo}
                                    </span>
                                    {suggestion.target.capacityConfidence === 'unknown' ? (
                                        <CapacityConfidenceBadge
                                            confidence="unknown"
                                            approxText={t('warehouse.modules.consolidation.pullIn.capacityApprox', {}, 'ca.')}
                                            unknownText={t('warehouse.modules.consolidation.pullIn.capacityUnknown', {}, 'ukjent — bekreft manuelt')}
                                        />
                                    ) : (
                                        <>
                                            <FillMeter pct={suggestion.target.fillPctAfter} />
                                            <span className="text-xs text-gray-600 whitespace-nowrap">
                                                {suggestion.target.currentQuantity} / {suggestion.target.maxQuantity}
                                                {suggestion.target.capacityConfidence === 'estimated' && (
                                                    <span className="ml-1 text-yellow-700">
                                                        ({t('warehouse.modules.consolidation.pullIn.capacityApprox', {}, 'ca.')})
                                                    </span>
                                                )}
                                            </span>
                                        </>
                                    )}
                                    {onUpdateCapacity && needsCapacity && (
                                        <button
                                            onClick={() => onUpdateCapacity({
                                                binCode,
                                                itemNo: suggestion.itemNo,
                                                itemDescription: suggestion.description1,
                                                currentQuantity: suggestion.target.currentQuantity
                                            })}
                                            className="ml-auto flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-cyan-700 border border-cyan-200 hover:bg-cyan-50 min-h-[36px] whitespace-nowrap flex-shrink-0"
                                            title={t('warehouse.modules.consolidation.capacity.updateHint', {}, 'Det er plass? Oppdater kapasiteten')}
                                        >
                                            <Layers className="h-3.5 w-3.5" />
                                            {t('warehouse.modules.consolidation.capacity.update', {}, 'Oppdater kapasitet')}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Kildeliste per forslag (gruppert per vare) */}
            {suggestions.length === 0 ? (
                <div className="bg-white rounded-lg p-6 shadow-sm border text-center">
                    <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                        {t('warehouse.modules.consolidation.pullIn.empty', {}, 'Ingen forslag for denne lokasjonen')}
                    </p>
                </div>
            ) : (
                <>
                    {suggestions.map((suggestion, index) => (
                        <div key={`${suggestion.itemNo}-src-${index}`} className="bg-white rounded-lg p-4 shadow-sm border">
                            <div className="flex items-center gap-2 mb-1">
                                <Package className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                                <span className="font-mono text-sm font-semibold text-gray-900 break-all">
                                    {suggestion.itemNo}
                                </span>
                            </div>
                            {suggestion.description1 && (
                                <div className="text-xs text-gray-500 mb-2 break-words">{suggestion.description1}</div>
                            )}
                            {suggestion.warnings.length > 0 && (
                                <div className="text-xs text-orange-600 mb-2 break-words">
                                    {suggestion.warnings.join(' · ')}
                                </div>
                            )}
                            <SourceList
                                sources={suggestion.sources}
                                selectionFor={(fromBinCode) => selectionFor(index, fromBinCode)}
                                onToggle={(fromBinCode) => toggle(index, fromBinCode)}
                                onQtyChange={(fromBinCode, qty) => setQty(index, fromBinCode, qty)}
                            />
                        </div>
                    ))}

                    <button
                        onClick={handleConfirm}
                        disabled={selectedCount === 0}
                        className={`w-full py-4 rounded-lg font-semibold text-base min-h-[44px] transition-colors ${
                            selectedCount > 0
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {t('warehouse.modules.consolidation.pullIn.moveNow', { '0': selectedCount }, `Opprett utleggsordre (${selectedCount} linjer)`)}
                    </button>
                </>
            )}
        </div>
    );
};
